import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Workout from './pages/Workout';
import History from './pages/History';
import SessionDetail from './pages/SessionDetail';
import Stats from './pages/Stats';
import Exercises from './pages/Exercises';
import Login from './pages/Login';
import Programs, { ProgramEditor } from './pages/Programs';
import Tools from './pages/Tools';
import Trophies from './pages/Trophies';
import Changelog from './pages/Changelog';
import Users from './pages/Users';
import Backup from './pages/Backup';
import Body from './pages/Body';
import SyncBanner from './components/SyncBanner';
import { clearQueue } from './lib/offline';
import PasswordModal from './components/PasswordModal';

// primary : toujours visibles (barre du bas sur mobile) ; les autres sont dans le menu « Plus »
const NAV = [
  { to: '/', icon: '🏠', label: 'Accueil', primary: true },
  { to: '/workout', icon: '💪', label: 'Séance', primary: true },
  { to: '/history', icon: '📋', label: 'Historique', primary: true },
  { to: '/stats', icon: '📊', label: 'Stats', primary: true },
  { to: '/programs', icon: '🗂️', label: 'Programmes' },
  { to: '/exercises', icon: '🏗️', label: 'Exercices' },
  { to: '/body', icon: '⚖️', label: 'Corps' },
  { to: '/tools', icon: '🧮', label: 'Outils' },
  { to: '/trophies', icon: '🏆', label: 'Trophées' },
  { to: '/changelog', icon: '📝', label: 'Versions' },
  { to: '/users', icon: '👥', label: 'Utilisateurs', adminOnly: true },
  { to: '/backup', icon: '💾', label: 'Sauvegarde', adminOnly: true },
];

const linkClass = ({ isActive }) => 'nav-link' + (isActive ? ' active' : '');

function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const ref = useRef(null);
  const active = items.some((n) => pathname.startsWith(n.to));

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="more-menu" ref={ref}>
      <button type="button" className={'nav-link' + (active || open ? ' active' : '')} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="icon">⋯</span>
        <span className="label">Plus</span>
      </button>
      {open && (
        <>
          <div className="more-backdrop" onClick={() => setOpen(false)} />
          <div className="more-panel" role="menu">
            <div className="more-grid">
              {items.map((n) => (
                <NavLink key={n.to} to={n.to} className={({ isActive }) => 'more-item' + (isActive ? ' active' : '')} role="menuitem">
                  <span className="icon">{n.icon}</span>
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function App() {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(() => localStorage.getItem('muscuapp_user'));
  const [role, setRole] = useState(() => localStorage.getItem('muscuapp_role'));
  const [pwdOpen, setPwdOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    document.body.classList.toggle('theme-light', theme === 'light');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleLogin = (username) => {
    queryClient.clear();
    setRole(localStorage.getItem('muscuapp_role'));
    setUser(username);
  };

  const handleLogout = () => {
    localStorage.removeItem('muscuapp_token');
    localStorage.removeItem('muscuapp_user');
    localStorage.removeItem('muscuapp_role');
    clearQueue();
    navigator.serviceWorker?.controller?.postMessage('clear-api-cache');
    queryClient.clear();
    setUser(null);
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const visible = NAV.filter((n) => !n.adminOnly || role === 'admin');
  const primary = visible.filter((n) => n.primary);
  const secondary = visible.filter((n) => !n.primary);
  const navLinks = primary.map((n) => (
    <NavLink key={n.to} to={n.to} end={n.to === '/'} className={linkClass} title={n.label}>
      <span className="icon">{n.icon}</span>
      <span className="label">{n.label}</span>
    </NavLink>
  ));

  return (
    <BrowserRouter>
      <div className="app-layout">
        <header className="topbar">
          <div className="topbar-inner">
            <NavLink to="/" className="brand">
              <span className="brand-badge">🏋️</span>
              <span className="brand-copy">
                <span className="brand-name">Muscu<span>App</span></span>
                <span className="brand-sub">Force &amp; Entraînement</span>
              </span>
            </NavLink>
            <nav className="top-nav">{navLinks}<MoreMenu items={secondary} /></nav>
            <div className="top-actions">
              <button
                className="icon-btn"
                onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
                title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
              <span className="icon-btn user-chip">👤 {user}</span>
              <button className="icon-btn" onClick={() => setPwdOpen(true)} title="Changer mon mot de passe">🔑</button>
              <button className="icon-btn" onClick={handleLogout} title="Déconnexion" aria-label="Déconnexion">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            </div>
          </div>
        </header>
        <SyncBanner />
        {pwdOpen && <PasswordModal onClose={() => setPwdOpen(false)} />}
        <nav className="bottom-nav">{navLinks}<MoreMenu items={secondary} /></nav>
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/workout" element={<Workout />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/programs/:id" element={<ProgramEditor />} />
            <Route path="/body" element={<Body />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/trophies" element={<Trophies />} />
            <Route path="/history" element={<History />} />
            <Route path="/history/:id" element={<SessionDetail />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/exercises" element={<Exercises />} />
            {role === 'admin' && <Route path="/users" element={<Users />} />}
            {role === 'admin' && <Route path="/backup" element={<Backup />} />}
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}
